import React, { useState } from "react";
import {
  Container,
  Grid,
  TextField,
  Button,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Tooltip,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { createEnquiry, updateEnquiry } from "../../../Services/Services";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { CircularProgress } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EditNoteIcon from "@mui/icons-material/EditNote";

export default function EnquiryForm() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation();

  // Constants
  const initialLineItem = { description: "", quantity: "", unit: "" };
  const initialNote = { note_text: "" };

  const getSelectedDivision = () => {
    try {
      const stored = localStorage.getItem("selectedDivision");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error("Failed to parse selectedDivision:", e);
      return null;
    }
  };

  const storedDivision = getSelectedDivision();

  // Helper function
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
    try {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0];
    } catch {
      return "";
    }
  };

  // ✅ Get enquiry data ONCE - this won't change on re-renders
  const [enquiryData] = useState(() => {
    const data = location.state?.data ?? null;
    if (data) {
      console.log("Edit mode - Enquiry data loaded:", data); // Logs only once
    }
    return data;
  });

  const isEditMode = !!enquiryData;

  // ✅ Initialize all states with prefilled values
  const [formData, setFormData] = useState(() => ({
    customer_name: enquiryData?.customer_name || "",
    company_name: enquiryData?.company_name || "",
    email: enquiryData?.email || "",
    phone: enquiryData?.phone || "",
    address: enquiryData?.address || "",
    city: enquiryData?.city || "",
    state: enquiryData?.state || "",
    country: enquiryData?.country || "India",
    enquiry_source: enquiryData?.enquiry_source || "",
    enq_type: enquiryData?.enq_type || "",
    follow_up_date: formatDateForInput(enquiryData?.follow_up_date),
    status: enquiryData?.status || "New",
    division: storedDivision?.division_id || ""
  }));

  const [lineItems, setLineItems] = useState(() => {
    if (enquiryData?.line_items?.length > 0) {
      return enquiryData.line_items.map((item) => ({
        description: item.description || "",
        quantity: item.quantity?.toString() || "",
        unit: item.unit || "",
      }));
    }
    return [{ ...initialLineItem }];
  });

  const [notes, setNotes] = useState(() => {
    if (enquiryData?.notes?.length > 0) {
      return enquiryData.notes.map((note) => ({
        note_text: note.note_text || "",
      }));
    }
    return [{ ...initialNote }];
  });

  const [errors, setErrors] = useState({ email: "", phone: "" });
  const [loading, setLoading] = useState(false);

  // Handlers
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "phone" && !/^\d*$/.test(value)) return;

    setFormData((prev) => ({ ...prev, [name]: value }));

    let errorMsg = "";
    if (name === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (value && !emailRegex.test(value)) {
        errorMsg = "Invalid email format (e.g., abc@gmail.com)";
      }
    }
    if (name === "phone") {
      if (value.length === 10) {
        const phoneRegex = /^[6-9]\d{9}$/;
        if (!phoneRegex.test(value)) {
          errorMsg = "Phone number must start with 6-9";
        }
      } else if (value.length > 10) {
        errorMsg = "Phone number can't exceed 10 digits";
      } else if (value.length > 0) {
        errorMsg = "Phone number must be 10 digits";
      }
    }
    setErrors((prev) => ({ ...prev, [name]: errorMsg }));
  };

  const handleLineItemChange = (index, field, value) => {
    setLineItems((prev) => {
      const updated = [...prev];
      updated[index][field] = value;
      return updated;
    });
  };

  const handleNoteChange = (index, value) => {
    setNotes((prev) => {
      const updated = [...prev];
      updated[index].note_text = value;
      return updated;
    });
  };

  const addLineItem = () => {
    if (lineItems.length >= 20) {
      toast.warning("Maximum 20 line items allowed!");
      return;
    }
    setLineItems((prev) => [...prev, { ...initialLineItem }]);
  };

  const addNote = () => {
    if (notes.length >= 10) {
      toast.warning("Maximum 10 notes allowed!");
      return;
    }
    setNotes((prev) => [...prev, { ...initialNote }]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length === 1) {
      toast.error("At least one line item is required!");
      return;
    }
    setLineItems((prev) => prev.filter((_, i) => i !== index));
  };

  const removeNote = (index) => {
    if (notes.length === 1) {
      setNotes([{ note_text: "" }]);
      return;
    }
    setNotes((prev) => prev.filter((_, i) => i !== index));
  };

  const validateFields = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[6-9]\d{9}$/;

    const newErrors = {
      email: emailRegex.test(formData.email) ? "" : "Invalid email format",
      phone: phoneRegex.test(formData.phone) ? "" : "Enter valid 10-digit phone",
    };
    setErrors(newErrors);

    let lineItemsValid = true;
    lineItems.forEach((item, index) => {
      if (!item.description.trim() || !item.quantity || !item.unit.trim()) {
        lineItemsValid = false;
        toast.error(`Please fill all fields in line item ${index + 1}`);
      }
    });

    const hasNotes = notes.some((note) => note.note_text.trim() !== "");
    if (!hasNotes) {
      toast.error("Please add at least one note");
    }

    return !newErrors.email && !newErrors.phone && lineItemsValid && hasNotes;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateFields()) return;

    const userId = localStorage.getItem("user_id");
    if (!userId) {
      toast.error("User not logged in.");
      return;
    }

    const filteredNotes = notes.filter((note) => note.note_text.trim() !== "");
    const dataToSend = {
      ...formData,
      line_items: lineItems,
      notes: filteredNotes,
      created_by: parseInt(userId),
    };

    setLoading(true);
    try {
      if (isEditMode) {
        await updateEnquiry(enquiryData.enquiry_id, dataToSend);
        toast.success("Enquiry updated successfully!");
      } else {
        await createEnquiry(dataToSend);
        toast.success("Enquiry submitted successfully!");
      }
      setTimeout(() => navigate("/dashboard/Enquiry"), 1500);
    } catch (error) {
      console.error("Error:", error);
      toast.error(isEditMode ? "Failed to update!" : "Failed to submit!");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (isEditMode && enquiryData) {
      setFormData({
        customer_name: enquiryData.customer_name || "",
        company_name: enquiryData.company_name || "",
        email: enquiryData.email || "",
        phone: enquiryData.phone || "",
        address: enquiryData.address || "",
        city: enquiryData.city || "",
        state: enquiryData.state || "",
        country: enquiryData.country || "India",
        enquiry_source: enquiryData.enquiry_source || "",
        enq_type: enquiryData.enq_type || "",
        follow_up_date: formatDateForInput(enquiryData.follow_up_date),
        status: enquiryData.status || "New",
        division: storedDivision?.division_id || ""
      });
      if (enquiryData.line_items?.length > 0) {
        setLineItems(
          enquiryData.line_items.map((item) => ({
            description: item.description || "",
            quantity: item.quantity?.toString() || "",
            unit: item.unit || "",
          }))
        );
      }
      if (enquiryData.notes?.length > 0) {
        setNotes(
          enquiryData.notes.map((note) => ({ note_text: note.note_text || "" }))
        );
      }
    } else {
      setFormData({
        customer_name: "",
        company_name: "",
        email: "",
        phone: "",
        address: "",
        city: "",
        state: "",
        country: "India",
        enquiry_source: "",
        enq_type: "",
        follow_up_date: "",
        status: "New",
      });
      setLineItems([{ ...initialLineItem }]);
      setNotes([{ ...initialNote }]);
    }
    setErrors({ email: "", phone: "" });
  };

  return (
    <Container maxWidth="xl" disableGutters sx={{ mt: 0, pt: 0, px: 0 }}>
      <ToastContainer />
      <form onSubmit={handleSubmit} autoComplete="off">
        {/* BUTTONS SECTION */}
        {isMobile ? (
          <Grid container spacing={2} sx={{ mb: 2, mt: -3 }}>
            <Grid item xs={6}>
              <Button
                fullWidth
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                sx={{ height: 40 }}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : "Submit"}
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={() => navigate("/dashboard/Enquiry")}
                sx={{ height: 40 }}
              >
                Back
              </Button>
            </Grid>
          </Grid>
        ) : (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, mt: -3 }}>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              {isEditMode ? "Edit Enquiry" : "Create Enquiry"}
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button type="submit" variant="contained" color="primary" disabled={loading}>
                {loading ? <CircularProgress size={20} color="inherit" /> : "Submit Enquiry"}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => navigate("/dashboard/Enquiry")}
              >
                ← Back
              </Button>
            </Box>
          </Box>
        )}

        {/* Customer Information Section */}
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" mb={2} sx={{ color: "#1976d2" }}>
              <EditNoteIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Customer Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Customer Name"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleChange}
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Company Name"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  inputProps={{ maxLength: 10 }}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  label="State"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  label="Country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Line Items Section */}
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ color: "#1976d2" }}>
                📋 Enquiry Items
              </Typography>
              <Tooltip title="Add Item">
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addLineItem}
                  size="small"
                >
                  Add Item
                </Button>
              </Tooltip>
            </Box>

            {isMobile ? (
              // Mobile View for Line Items
              <Box>
                {lineItems.map((item, index) => (
                  <Paper
                    key={index}
                    sx={{
                      mb: 2,
                      p: 2,
                      borderRadius: 2,
                      boxShadow: 1,
                      border: "1px solid #e0e0e0",
                      position: "relative",
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#666" }}>
                        Item #{index + 1}
                      </Typography>
                      {lineItems.length > 1 && (
                        <Tooltip title="Remove Item">
                          <IconButton
                            color="error"
                            onClick={() => removeLineItem(index)}
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <TextField
                        fullWidth
                        label="Description"
                        value={item.description}
                        onChange={(e) => handleLineItemChange(index, "description", e.target.value)}
                        multiline
                        minRows={2}
                        maxRows={4}
                        required
                        size="small"
                      />
                      <TextField
                        fullWidth
                        label="Quantity"
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleLineItemChange(index, "quantity", e.target.value)}
                        required
                        size="small"
                      />
                      <TextField
                        fullWidth
                        label="Unit"
                        value={item.unit}
                        onChange={(e) => handleLineItemChange(index, "unit", e.target.value)}
                        required
                        size="small"
                      />
                    </Box>
                  </Paper>
                ))}
              </Box>
            ) : (
              // Desktop View for Line Items
              <TableContainer component={Paper} sx={{ boxShadow: 0, border: "1px solid #e0e0e0" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                      <TableCell width="5%" sx={{ fontWeight: "bold" }}>#</TableCell>
                      <TableCell width="55%" sx={{ fontWeight: "bold" }}>Description</TableCell>
                      <TableCell width="15%" sx={{ fontWeight: "bold" }}>Quantity</TableCell>
                      <TableCell width="20%" sx={{ fontWeight: "bold" }}>Unit</TableCell>
                      <TableCell width="5%" align="center" sx={{ fontWeight: "bold" }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lineItems.map((item, index) => (
                      <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Enter item description"
                            value={item.description}
                            onChange={(e) => handleLineItemChange(index, "description", e.target.value)}
                            multiline
                            minRows={2}
                            maxRows={4}
                            required
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            placeholder="Qty"
                            value={item.quantity}
                            onChange={(e) => handleLineItemChange(index, "quantity", e.target.value)}
                            required
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Unit (kg, pcs, etc.)"
                            value={item.unit}
                            onChange={(e) => handleLineItemChange(index, "unit", e.target.value)}
                            required
                          />
                        </TableCell>
                        <TableCell align="center">
                          {lineItems.length > 1 && (
                            <Tooltip title="Remove Item">
                              <IconButton
                                color="error"
                                onClick={() => removeLineItem(index)}
                                size="small"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Notes Section */}
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ color: "#1976d2" }}>
                📝 Notes
              </Typography>
              <Tooltip title="Add Note">
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addNote}
                  size="small"
                >
                  Add Note
                </Button>
              </Tooltip>
            </Box>

            {isMobile ? (
              // Mobile View for Notes
              <Box>
                {notes.map((note, index) => (
                  <Paper
                    key={index}
                    sx={{
                      mb: 2,
                      p: 2,
                      borderRadius: 2,
                      boxShadow: 1,
                      border: "1px solid #e0e0e0",
                      position: "relative",
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#666" }}>
                        Note #{index + 1}
                      </Typography>
                      {notes.length > 1 && (
                        <Tooltip title="Remove Note">
                          <IconButton
                            color="error"
                            onClick={() => removeNote(index)}
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      maxRows={4}
                      label="Note"
                      value={note.note_text}
                      onChange={(e) => handleNoteChange(index, e.target.value)}
                      placeholder="Enter note here..."
                      size="small"
                    />
                  </Paper>
                ))}
              </Box>
            ) : (
              // Desktop View for Notes
              <Box>
                {notes.map((note, index) => (
                  <Box key={index} sx={{ mb: 2, position: "relative" }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#666", mr: 2 }}>
                        Note {index + 1}:
                      </Typography>
                      {notes.length > 1 && (
                        <Tooltip title="Remove Note">
                          <IconButton
                            color="error"
                            onClick={() => removeNote(index)}
                            size="small"
                            sx={{ ml: 'auto' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      maxRows={4}
                      placeholder="Enter note here..."
                      value={note.note_text}
                      onChange={(e) => handleNoteChange(index, e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#fafafa',
                        }
                      }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Enquiry Details Section */}
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" mb={2} sx={{ color: "#1976d2" }}>
              ⚙️ Enquiry Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Enquiry Type"
                  name="enquiry_source"
                  value={formData.enquiry_source || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      enquiry_source: e.target.value,
                    }))
                  }
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Follow Up Date"
                  name="follow_up_date"
                  value={formData.follow_up_date}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Enquiry Status"
                  name="status"
                  value={formData.status}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      status: e.target.value,
                    }))
                  }
                  required
                  size="small"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </form>
    </Container>
  );
}